import { Link } from 'react-router-dom';
import { ArrowRight, FileText, BookOpen, Video, HelpCircle, MessageSquare } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, CTAStrip } from './shared/blocks';
import SEO from './shared/SEO';
import { Button } from '../../components/ui/button';
import { BLOG_POSTS } from './content';

const RESOURCE_CATEGORIES = [
  { icon: BookOpen,   title: 'Guides',       desc: 'Step-by-step playbooks for teachers and admins.', href: '/help-center' },
  { icon: FileText,   title: 'Templates',    desc: 'Downloadable lesson plans, worksheets, blueprints.', href: '/blog' },
  { icon: Video,      title: 'Blog',         desc: 'Stories, research, and tactics from real classrooms.', href: '/blog' },
  { icon: HelpCircle, title: 'Help Center',  desc: 'Searchable knowledge base with how-to articles.', href: '/help-center' },
  { icon: MessageSquare, title: 'FAQ',       desc: 'Quick answers to the most common questions.', href: '/faq' },
];

export default function ResourcesPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Resources | Guides, templates, blog, help center"
        description="Resources for Indian teachers and principals — playbooks, downloadable templates, blog articles, and a searchable help center."
        path="/resources"
        keywords={['teacher resources', 'school resources', 'lesson plan templates', 'school ERP guide']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Resources', url: '/resources' }]}
      />
      <PageHero
        eyebrow="Resources"
        title="Everything you need to teach"
        accent="smarter."
        subtitle="Guides, templates, articles, and answers — curated by Indian educators."
        testid="resources-hero"
      />

      <Section>
        <SectionHeader eyebrow="By category" title="Browse the library." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {RESOURCE_CATEGORIES.map((r) => (
            <Link key={r.title} to={r.href} className="block rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all group">
              <r.icon className="h-6 w-6 text-mk-blue dark:text-mk-cyan mb-3" />
              <div className="font-bold text-lg">{r.title}</div>
              <p className="text-sm text-mk-muted dark:text-slate-300 mt-1 leading-relaxed">{r.desc}</p>
              <div className="mt-4 text-sm font-semibold text-mk-deep dark:text-mk-cyan flex items-center gap-1">Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></div>
            </Link>
          ))}
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeader eyebrow="From the blog" title="Latest reads." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {BLOG_POSTS.slice(0, 3).map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="block rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-5 hover:shadow-lg transition-all">
              <div className="text-xs uppercase tracking-wider text-mk-blue dark:text-mk-cyan mb-2">{p.category}</div>
              <h3 className="font-bold leading-snug">{p.title}</h3>
              <p className="text-sm text-mk-muted dark:text-slate-300 mt-2 leading-relaxed line-clamp-3">{p.excerpt}</p>
              <div className="mt-4 text-xs text-slate-500">{p.date} · {p.readTime} read</div>
            </Link>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to="/blog">
            <Button variant="ghost" className="text-mk-deep dark:text-mk-cyan font-semibold">See all articles <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </Link>
        </div>
      </Section>

      <CTAStrip
        title="Still have questions?"
        subtitle="The founder reads every message — no chat-bot deflection."
        primary={{ label: 'Contact us', href: '/contact' }}
        secondary={{ label: 'WhatsApp', wa: 'Hi! I came from the ActanonEdu resources page.' }}
        testid="resources-final-cta"
      />
    </MarketingLayout>
  );
}
